/* eslint-disable camelcase */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { addToCartDataLayer } from "@sunSource/Components/AddToCartFunction";
import INTHubSpotComponent from "@sunSource/Components/INTHubSpotComponent";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

export interface RequestForQuoteModalStyles {
    requestForQuoteModal?: ModalPresentationProps;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { onePageCheckout, addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        cart,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        userPhoneNumber: state.context.session?.properties?.phoneNumber ?? state.context.session?.properties?.phone,
        userCompanyName: state.context.session?.properties?.companyName,
        userName: state.context.session?.userName,
        userFirstName: state.context.session?.firstName,
        userLastName: state.context.session?.lastName,
        isAuthenticated: state.context.session?.isAuthenticated,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export const productBuyNowButtonStyles: ButtonPresentationProps = {
    size: 36,
    variant: "primary",
    typographyProps: {
        css: css`
            font-size: 15px;
            font-weight: bold;
        `,
    },
    css: css`
        margin-top: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-transform: capitalize;
        width: 100%;
        &:disabled {
            color: #fff;
            border-color: #cccccc;
            background: #cccccc;
            > span {
                color: #fff;
                font-weight: unset;
            }
            path {
                stroke: #fff;
            }
        }
        &:hover {
            background: #000000;
            box-shadow: 0 2px 5px 0 #d5d9d980;
            > span {
                text-decoration: none;
            }
        }
    `,
};

export const rfqModalStyles: RequestForQuoteModalStyles = {
    requestForQuoteModal: {
        size: 650,
        cssOverrides: {
            modalBody: css`
                .RFQ-container {
                    background: rgba(225, 221, 226, 0.3);
                }
            `,
            modalTitle: css`
                padding: 0px;
                border: none;
                > ${TypographyStyle} {
                    color: ${getColor("secondary")};
                    font-size: 1rem;
                }
                + [class^="ModalContent-sc-"] {
                    padding: 10px 30px;
                }
                #CloseModalButton {
                    position: absolute;
                    height: 33px;
                    top: 25px;
                    right: 25px;
                    width: 33px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: #ffffff !important;
                    border: none !important;
                    min-height: 33px;
                    > span {
                        color: #275aa8;
                        position: unset;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }
                    svg {
                        height: 21px;
                        width: 21px;
                    }
                }
            `,
        },
    },
};

const styles = rfqModalStyles;

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const ProductBuyNowButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired, properties, productNumber, brand },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, inventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    checkoutReviewAndSubmitPageUrl,
    userPhoneNumber,
    userCompanyName,
    userName,
    userFirstName,
    userLastName,
    isAuthenticated,
    onePageCheckout,
    onePageCheckoutPageUrl,
    cart,
    ...otherProps
}: Props) => {
    const history = useHistory();
    const toasterContext = useContext(ToasterContext);
    const [buttonStyles] = useState(() => mergeToNew(productBuyNowButtonStyles, extendedStyles));
    const [isRequestForQuoteModalOpen, setIsRequestForQuoteModalOpen] = useState(false);
    const [hubSpotFormKey, setHubSpotFormKey] = useState(0);

    const availability =
        inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;
    const isOrderNowButton = availability?.messageType === 2;

    const vendor = properties?.vendor
        ? `${productNumber}: ${properties.vendor}`
        : `${productNumber}: ${siteMessage("SunSource")}`;

    const supplier = brand?.name;
    const model = productNumber;

    const handleModalOpen = () => {
        setHubSpotFormKey(currentKey => currentKey + 1);

        const stickySideBarContent = document.getElementById("stickySideBarContent");
        if (stickySideBarContent) {
            stickySideBarContent.style.position = "";
            stickySideBarContent.style.top = "";
            stickySideBarContent.style.bottom = "";
            stickySideBarContent.style.transform = "";
            stickySideBarContent.style.width = "";
        }

        const staticHubSpotForm = document.getElementById("hubspotFormModal");
        if (staticHubSpotForm) {
            staticHubSpotForm.classList.add("modalContent");
            document.getElementById("modalOverlay")?.classList.add("modalOverlay");
            const modalHubspot = document.querySelector("#hubspot");

            const hubSpot = (window as any).hbspt;
            modalHubspot?.replaceChildren();
            hubSpot?.forms.create({
                region: siteMessage("na1"),
                portalId: siteMessage("24145943"),
                formId: siteMessage("5170b21b-d7df-4080-8988-d87498bb098d"),
                target: "#hubspot",
            });


            const vendorField = modalHubspot?.getElementsByClassName("hs_vendor");
            if (vendorField && vendorField.length > 0) {
                vendorField[0].setAttribute(
                    "style",
                    "display: block; position: absolute; left: -5000px; top: -5000px;",
                );
            }
            const vendorHiddenField = modalHubspot?.querySelector('input[name="vendor"]');
            vendorHiddenField?.setAttribute("type", "");

            if (vendorHiddenField) {
                vendorHiddenField?.setAttribute("value", vendor);
            }

            const supplier_formsubmitHiddenField = modalHubspot?.querySelector('input[name="supplier_formsubmit"]');
            if (supplier_formsubmitHiddenField) {
                supplier_formsubmitHiddenField?.setAttribute("type", "text");
                supplier_formsubmitHiddenField?.setAttribute("placeholder", supplier ? "Loading..." : "Suppliers");
            }

            const model_seriesHiddenField = modalHubspot?.querySelector('input[name="model_series"]');
            if (model_seriesHiddenField) {
                model_seriesHiddenField?.setAttribute("type", "text");
                model_seriesHiddenField?.setAttribute("placeholder", model ? "Loading..." : "Model");
            }

            const recent_conversion_form_page_url = modalHubspot?.querySelector(
                'input[name="recent_conversion_form_page_url"]',
            );
            if (recent_conversion_form_page_url) {
                const pageUrl = window.location.href;
                recent_conversion_form_page_url?.setAttribute("value", pageUrl);
            }

            const recent_conversion_form_page_title = modalHubspot?.querySelector(
                'input[name="recent_conversion_form_page_title"]',
            );
            if (recent_conversion_form_page_title) {
                const pageTitle = document.title;
                recent_conversion_form_page_title?.setAttribute("value", pageTitle);
            }

            if (isAuthenticated && productId) {
                const model_firstname = modalHubspot?.querySelector(
                    'input[name="firstname"]',
                ) as HTMLInputElement | null;
                if (model_firstname) {
                    model_firstname?.setAttribute("placeholder", userFirstName ? "Loading..." : "First Name");
                }
                const model_lastname = modalHubspot?.querySelector('input[name="lastname"]') as HTMLInputElement | null;
                if (model_lastname) {
                    model_lastname?.setAttribute("placeholder", userLastName ? "Loading..." : "Last Name");
                }
                const model_phone = modalHubspot?.querySelector('input[name="phone"]') as HTMLInputElement | null;
                if (model_phone) {
                    model_phone?.setAttribute("placeholder", userPhoneNumber ? "Loading..." : "Phone Numbe");
                }
                const model_email = modalHubspot?.querySelector('input[name="email"]') as HTMLInputElement | null;
                if (model_email) {
                    model_email?.setAttribute("placeholder", userPhoneNumber ? "Loading..." : "Email");
                }
                const model_company = modalHubspot?.querySelector('input[name="company"]') as HTMLInputElement | null;
                if (model_company) {
                    model_company?.setAttribute("placeholder", userCompanyName ? "Loading..." : "Company Name");
                }
            }

            if (supplier || model) {
                setTimeout(() => {
                    const supplier_formsubmitHiddenField = modalHubspot?.querySelector(
                        'input[name="supplier_formsubmit"]',
                    );
                    if (supplier_formsubmitHiddenField) {
                        supplier_formsubmitHiddenField?.setAttribute("type", "text");
                        supplier_formsubmitHiddenField?.setAttribute("value", `${supplier}`);
                        supplier_formsubmitHiddenField?.setAttribute("placeholder", "Suppliers");
                    }

                    const model_seriesHiddenField = modalHubspot?.querySelector('input[name="model_series"]');
                    if (model_seriesHiddenField) {
                        model_seriesHiddenField?.setAttribute("type", "text");
                        model_seriesHiddenField?.setAttribute("value", `${model}`);
                        model_seriesHiddenField?.setAttribute("placeholder", "Model");
                    }

                    if (isAuthenticated) {
                        const model_firstname = modalHubspot?.querySelector(
                            'input[name="firstname"]',
                        ) as HTMLInputElement | null;
                        if (model_firstname) {
                            model_firstname.value = userFirstName || "";
                            model_firstname.focus();
                        }
                        const model_lastname = modalHubspot?.querySelector(
                            'input[name="lastname"]',
                        ) as HTMLInputElement | null;
                        if (model_lastname) {
                            model_lastname.value = userLastName || "";
                            model_lastname.focus();
                        }
                        const model_phone = modalHubspot?.querySelector(
                            'input[name="phone"]',
                        ) as HTMLInputElement | null;
                        if (model_phone) {
                            model_phone.value = userPhoneNumber || "";
                            model_phone.focus();
                        }
                        const model_email = modalHubspot?.querySelector(
                            'input[name="email"]',
                        ) as HTMLInputElement | null;
                        if (model_email) {
                            model_email.value = userName || "";
                            model_email.focus();
                        }
                        const model_company = modalHubspot?.querySelector(
                            'input[name="company"]',
                        ) as HTMLInputElement | null;
                        if (model_company) {
                            model_company.value = userCompanyName || "";
                            model_company.focus();
                        }
                    }
                }, 2000);
            }
        } else {
            setIsRequestForQuoteModalOpen(true);
        }
    };
    const handleModalClose = () => {
        const stickySideBarContent = document.getElementById("stickySideBarContent");
        if (stickySideBarContent) {
            stickySideBarContent.style.position = "relative";
            stickySideBarContent.style.top = "0px";
            stickySideBarContent.style.bottom = "auto";
            stickySideBarContent.style.transform = "none";
        }
        setIsRequestForQuoteModalOpen(false);
    };

    if (
        quoteRequired === true ||
        !pricing ||
        (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0)
    ) {
        return (
            <>
                <Button
                    {...buttonStyles}
                    onClick={handleModalOpen}
                    disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                    {...otherProps}
                >
                    {labelOverride ?? translate("Quote Now")}
                </Button>

                <Modal
                    {...styles.requestForQuoteModal}
                    isOpen={isRequestForQuoteModalOpen}
                    handleClose={handleModalClose}
                    data-test-selector="requestForQuoteModal"
                >
                                        {isRequestForQuoteModalOpen && (
                        <INTHubSpotComponent
                            key={hubSpotFormKey}
                            productId={productId}
                            model={model}
                            vendor={vendor}
                            supplier={supplier}
                        />
                    )}

                </Modal>
            </>
        );
    }

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
            addToCartDataLayer(cartline);

            if (onePageCheckout && onePageCheckoutPageUrl) {
                if (!cart?.isAuthenticated) {
                    if (siteMessage("newOnePageCheckout") === "true") {
                        history.push("Newonepagecheckout");
                    } else {
                        history.push(onePageCheckoutPageUrl!);
                    }
                } else {
                    history.push(
                        cart?.isAwaitingApproval
                            ? `${onePageCheckoutPageUrl}?cartId=${cart?.id}`
                            : onePageCheckoutPageUrl,
                    );
                }
            } else {
                history.push(checkoutReviewAndSubmitPageUrl!);
            }
        }
    };

    return (
        <Button
            {...buttonStyles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            {...otherProps}
        >
            {isOrderNowButton ? translate("Order Now") : translate("Buy Now")}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductBuyNowButton));